import { ReactNode, createContext, useContext, useRef, useState } from 'react'

import { Button } from './Button'
import { Modal } from './Modal'
import styles from './ConfirmDialog.module.scss'

type ConfirmOptions = { title: string; message: string; confirmLabel?: string }

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null)

  const confirm = (nextOptions: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      resolverRef.current = resolve
      setOptions(nextOptions)
    })

  const close = (confirmed: boolean) => {
    resolverRef.current?.(confirmed)
    resolverRef.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={options !== null}
        onClose={() => close(false)}
        size="small"
        id="customConfirmModal"
        dialogClassName={styles.dialog}
        showCloseButton={false}
        header={
          <h4 className="modal-title" id="customConfirmModalTitle">
            {options?.title}
          </h4>
        }
        footer={
          <>
            <Button variant="default" data-testid="confirm-cancel" onClick={() => close(false)}>
              Cancelar
            </Button>
            <Button variant="danger" data-testid="confirm-accept" onClick={() => close(true)}>
              {options?.confirmLabel ?? 'Eliminar'}
            </Button>
          </>
        }
      >
        <span id="customConfirmModalBody">{options?.message}</span>
      </Modal>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const confirm = useContext(ConfirmContext)
  if (!confirm) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return confirm
}
